import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Film, Search, Star, Sparkles, Loader2, Plus, Eye, ArrowUp, ArrowDown, List, ThumbsUp, Meh, ThumbsDown, Check, Trophy, RefreshCw, Clapperboard, Calendar, Award, TrendingUp, Crown, Globe, ListOrdered, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { SeriesDetailsDialog } from '../components/SeriesDetailsDialog';
import { RankNewSeriesDialog } from '../components/RankNewSeriesDialog';
import { AppHeader } from '../components/AppHeader';
import { MediaCard } from '../components/MediaCard';
import { AddToListDialog } from '../components/AddToListDialog';
import { searchMedia, discoverMedia, getGenres, toTMDBFormat, getTMDBDetails, MediaType } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'popularity.desc', label: 'Most Popular' },
  { value: 'vote_average.desc', label: 'Highest Rated' },
  { value: 'primary_release_date.desc', label: 'Newest First' },
  { value: 'primary_release_date.asc', label: 'Oldest First' }
];

const COLLECTIONS = [
  { value: 'for-you', label: 'For You', icon: Sparkles, description: 'Personal recommendations based on your taste' },
  { value: 'award_season_2026', label: '2026 Award Season', icon: Award, description: 'Oscar, Golden Globe & BAFTA nominees' },
  { value: 'trending', label: 'Trending This Week', icon: TrendingUp, description: 'What everyone is watching' },
  { value: 'classics', label: 'Classic Must-Watch', icon: Crown, description: 'Timeless film classics' }
];

export default function FilmsDiscoverPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortBy, setSortBy] = useState('popularity.desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mode, setMode] = useState('discover');
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDetails, setSeriesDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // New filter states
  const [englishOnly, setEnglishOnly] = useState(false);
  const [activeCollection, setActiveCollection] = useState(null);
  
  // User's watchlists
  const [watchlists, setWatchlists] = useState([]);
  
  // Add to List state
  const [addToListItem, setAddToListItem] = useState(null);
  const [selectedListId, setSelectedListId] = useState(null);
  const [addingToList, setAddingToList] = useState(false);
  
  // Add & Rank state
  const [isRankNewOpen, setIsRankNewOpen] = useState(false);
  const [rankingListSeries, setRankingListSeries] = useState([]);
  
  // Mark as Watched state
  const [watchedItem, setWatchedItem] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [savingWatched, setSavingWatched] = useState(false);
  const [primaryGenre, setPrimaryGenre] = useState(null);
  const [customGenre, setCustomGenre] = useState('');
  
  // Comparison ranking state
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [determinedRank, setDeterminedRank] = useState(null);
  
  // First watch and comment state
  const [isFirstWatch, setIsFirstWatch] = useState(true);
  const [isBigScreen, setIsBigScreen] = useState(false);
  const [watchDate, setWatchDate] = useState('');
  const [watchComment, setWatchComment] = useState('');
  
  // AI Recommendations state
  const [personalRecommendations, setPersonalRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    fetchGenres();
    fetchWatchlists();
    
    // Check if we should show personal recommendations
    const collectionParam = searchParams.get('collection');
    if (collectionParam === 'for-you') {
      setActiveCollection('for-you');
      fetchPersonalRecommendations();
    }
    
    // Check if we should show a specific item (from social feed click)
    const itemParam = searchParams.get('item');
    if (itemParam) {
      const itemId = parseInt(itemParam);
      if (!isNaN(itemId)) {
        // Fetch and display the item
        handleViewDetails({ tmdb_id: itemId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode === 'discover' && activeCollection !== 'for-you') {
      fetchDiscover();
    } else if (activeCollection === 'for-you') {
      fetchPersonalRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, sortBy, page, mode, englishOnly, activeCollection]);
  
  const fetchPersonalRecommendations = async () => {
    setLoadingRecommendations(true);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/recommendations/ai/movie`, { withCredentials: true });
      if (response.data.unlocked && response.data.recommendations) {
        setPersonalRecommendations(response.data);
        setResults(response.data.recommendations);
        setTotalPages(1);
        setMode('recommendations');
      } else {
        // Not enough watched - show message via personalRecommendations
        setPersonalRecommendations(response.data);
        setResults([]);
        setTotalPages(1);
        setMode('recommendations');
      }
    } catch (error) {
      toast.error('Failed to load AI recommendations');
      setActiveCollection(null);
      fetchDiscover();
    } finally {
      setLoadingRecommendations(false);
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      // Use canonical media API for genres
      const genres = await getGenres(MediaType.MOVIE);
      setGenres(genres || []);
    } catch {
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/tmdb/genres/movie`, { withCredentials: true });
        setGenres(response.data.genres || []);
      } catch {
        // eslint-disable-next-line no-empty -- Intentional: Genres are non-critical UI enhancement, silent fail is acceptable
      }
    }
  };

  const fetchWatchlists = async () => {
    try {
      const response = await axios.get(`${API}/watchlists?media_category=movie`, { withCredentials: true });
      setWatchlists(response.data || []);
    } catch {
      // eslint-disable-next-line no-empty -- Intentional: Watchlists dropdown is non-critical, silent fail is acceptable
    }
  };

  const fetchDiscover = async () => {
    setLoading(true);
    try {
      // For collections (award_season, trending, classics), use old API which has curated data
      if (activeCollection && activeCollection !== 'for-you') {
        const params = new URLSearchParams({
          page: page.toString(),
          sort_by: sortBy
        });
        if (selectedGenre !== 'all' && !activeCollection) {
          params.append('with_genres', selectedGenre);
        }
        if (englishOnly) {
          params.append('english_only', 'true');
        }
        params.append('collection', activeCollection);
        const response = await axios.get(`${API}/tmdb/discover/movie?${params}`, { withCredentials: true });
        setResults(response.data.results || []);
        setTotalPages(response.data.total_pages || 1);
        setMode('discover');
        return;
      }
      
      // Use canonical media API for regular discovery
      // Pass genre ID (not name) since TMDB provider expects ID
      const genreId = selectedGenre !== 'all' ? selectedGenre : null;
      
      const result = await discoverMedia(MediaType.MOVIE, {
        page,
        limit: 24,
        genre: genreId,
        englishOnly: englishOnly
      });
      
      // Convert canonical items to TMDB format for compatibility
      const films = result.items.map(item => {
        const tmdbFormat = toTMDBFormat(item);
        return {
          tmdb_id: tmdbFormat.tmdb_id || item.external_ids?.tmdb_id,
          id: tmdbFormat.id,
          name: tmdbFormat.name,
          title: tmdbFormat.title,
          overview: tmdbFormat.overview,
          poster_path: tmdbFormat.poster_path,
          backdrop_path: tmdbFormat.backdrop_path,
          vote_average: tmdbFormat.vote_average,
          vote_count: tmdbFormat.vote_count,
          release_date: tmdbFormat.release_date,
          genre_ids: tmdbFormat.genre_ids,
          genres: tmdbFormat.genres,
          popularity: tmdbFormat.popularity,
          _canonical: item
        };
      });
      
      setResults(films);
      setTotalPages(result.totalPages || 1);
      setMode('discover');
    } catch (error) {
      console.error('Canonical API failed, falling back:', error);
      // Fallback to old API
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          sort_by: sortBy
        });
        if (selectedGenre !== 'all' && !activeCollection) {
          params.append('with_genres', selectedGenre);
        }
        if (englishOnly) {
          params.append('english_only', 'true');
        }
        if (activeCollection) {
          params.append('collection', activeCollection);
        }
        const response = await axios.get(`${API}/tmdb/discover/movie?${params}`, { withCredentials: true });
        setResults(response.data.results || []);
        setTotalPages(response.data.total_pages || 1);
        setMode('discover');
      } catch (e) {
        toast.error('Failed to load films');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      fetchDiscover();
      return;
    }
    setLoading(true);
    try {
      // Use canonical media API for search
      const result = await searchMedia(searchQuery, MediaType.MOVIE, 1, 24);
      
      // Convert canonical items to TMDB format for compatibility
      const films = result.items.map(item => {
        const tmdbFormat = toTMDBFormat(item);
        return {
          tmdb_id: tmdbFormat.tmdb_id || item.external_ids?.tmdb_id,
          id: tmdbFormat.id,
          name: tmdbFormat.name,
          title: tmdbFormat.title,
          overview: tmdbFormat.overview,
          poster_path: tmdbFormat.poster_path,
          backdrop_path: tmdbFormat.backdrop_path,
          vote_average: tmdbFormat.vote_average,
          vote_count: tmdbFormat.vote_count,
          release_date: tmdbFormat.release_date,
          genre_ids: tmdbFormat.genre_ids,
          genres: tmdbFormat.genres,
          popularity: tmdbFormat.popularity,
          _canonical: item
        };
      });
      
      setResults(films);
      setMode('search');
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      console.error('Canonical search failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/tmdb/search`, {
          params: { query: searchQuery, media_type: 'movie' },
          withCredentials: true
        });
        setResults(response.data.results || []);
        setMode('search');
        setTotalPages(1);
      } catch (e) {
        toast.error('Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (item) => {
    setSelectedSeries(item);
    setLoadingDetails(true);
    try {
      // Use canonical media API for details
      const tmdbId = item.tmdb_id || item.id;
      const details = await getTMDBDetails(tmdbId, 'movie');
      setSeriesDetails(details);
    } catch (error) {
      console.error('Canonical details failed, falling back:', error);
      // Fallback to old API
      try {
        const response = await axios.get(`${API}/tmdb/movie/${item.tmdb_id}`, { withCredentials: true });
        setSeriesDetails(response.data);
      } catch (e) {
        toast.error('Failed to load details');
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setMode('discover');
    setPage(1);
    setActiveCollection(null);
    fetchDiscover();
  };

  const handleCollectionClick = (collectionValue) => {
    if (activeCollection === collectionValue) {
      setActiveCollection(null);
    } else {
      setActiveCollection(collectionValue);
      setSelectedGenre('all');
    }
    setPage(1);
  };

  // Add to List handlers
  const handleOpenAddToList = (item, e) => {
    e?.stopPropagation();
    setAddToListItem(item);
    setSelectedListId(null);
  };

  // Handler for AddToListDialog component - receives (listId, item, position)
  const handleAddToListFromDialog = async (listId, item, position) => {
    setSelectedListId(listId);
    setAddingToList(true);
    try {
      await axios.post(`${API}/watchlists/${listId}/series`, {
        tmdb_id: item.tmdb_id,
        title: item.title,
        poster_path: item.poster_path,
        backdrop_path: item.backdrop_path,
        first_air_date: item.first_air_date,
        overview: item.overview,
        vote_average: item.vote_average,
        media_type: 'movie',
        position: position
      }, { withCredentials: true });
      
      toast.success(`Added "${item.title}" to list!`);
      setAddToListItem(null);
      setSelectedListId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    } finally {
      setAddingToList(false);
    }
  };

  // Handler for AddToListDialog "Add & Rank" - receives (list, item)
  const handleAddAndRankFromDialog = async (list, item) => {
    setSelectedListId(list.id);
    setAddingToList(true);
    try {
      const response = await axios.post(`${API}/watchlists/${list.id}/series`, {
        tmdb_id: item.tmdb_id,
        title: item.title,
        poster_path: item.poster_path,
        backdrop_path: item.backdrop_path,
        first_air_date: item.first_air_date,
        overview: item.overview,
        vote_average: item.vote_average,
        media_type: 'movie',
        position: 'bottom'
      }, { withCredentials: true });
      
      const listSeries = response.data.series || [];
      if (listSeries.length > 1) {
        setRankingListSeries(listSeries);
        setIsRankNewOpen(true);
      } else {
        toast.success(`Added "${item.title}" to list!`);
        setAddToListItem(null);
        setSelectedListId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
      setAddingToList(false);
    }
  };

  // Legacy handlers (kept for backward compatibility)
  const handleAddToList = async (position) => {
    if (!selectedListId || !addToListItem) return;
    await handleAddToListFromDialog(selectedListId, addToListItem, position);
  };

  const handleAddAndRank = async () => {
    if (!selectedListId || !addToListItem) return;
    const list = watchlists.find(l => l.id === selectedListId);
    if (list) await handleAddAndRankFromDialog(list, addToListItem);
  };

  const handleSaveRankOrder = async (orderedIds) => {
    try {
      const selectedList = watchlists.find(l => l.id === selectedListId);
      const isJointList = selectedList?.list_type === 'joint';
      
      if (isJointList) {
        await axios.put(`${API}/watchlists/${selectedListId}/my-ranking`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
      } else {
        await axios.put(`${API}/watchlists/${selectedListId}/reorder`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
      }
      
      toast.success(`"${addToListItem?.title}" added and ranked!`);
      setIsRankNewOpen(false);
      setAddToListItem(null);
      setSelectedListId(null);
      setRankingListSeries([]);
    } catch (error) {
      toast.error('Failed to save ranking');
    } finally {
      setAddingToList(false);
    }
  };

  // Mark as Watched handlers
  const handleOpenWatched = (item, e) => {
    e?.stopPropagation();
    
    // Normalize genres to array of strings
    // Handle multiple possible formats:
    // 1. item.genres as array of strings: ["Action", "Drama"]
    // 2. item.genres as array of objects: [{name: "Action"}, {name: "Drama"}]
    // 3. item.genre_ids as array of IDs that need mapping
    // 4. item._canonical.provider_data.genre_ids for canonical API items
    let itemGenres = [];
    
    if (item.genres && Array.isArray(item.genres) && item.genres.length > 0) {
      // Check if genres are objects with name property or plain strings
      if (typeof item.genres[0] === 'object' && item.genres[0]?.name) {
        itemGenres = item.genres.map(g => g.name).filter(Boolean);
      } else if (typeof item.genres[0] === 'string') {
        itemGenres = item.genres;
      }
    }
    
    // If no genres found, try mapping from genre_ids
    if (itemGenres.length === 0 && item.genre_ids && Array.isArray(item.genre_ids) && item.genre_ids.length > 0) {
      itemGenres = item.genre_ids
        .map(id => genres.find(g => g.id === id)?.name)
        .filter(Boolean);
    }
    
    // If still no genres, try canonical API's provider_data.genre_ids
    if (itemGenres.length === 0 && item._canonical?.provider_data?.genre_ids && Array.isArray(item._canonical.provider_data.genre_ids)) {
      itemGenres = item._canonical.provider_data.genre_ids
        .map(id => genres.find(g => g.id === id)?.name)
        .filter(Boolean);
    }
    
    const itemWithGenres = { ...item, genres: itemGenres };
    setWatchedItem(itemWithGenres);
    setSelectedGrade(null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setDeterminedRank(null);
    setIsFirstWatch(true);
    setIsBigScreen(false);
    setWatchDate(new Date().toISOString().split('T')[0]); // Default to today
    setWatchComment('');
    setPrimaryGenre(itemGenres[0] || null); // Default to first genre
    setCustomGenre(''); // Reset custom genre
  };

  const handleGradeSelect = (grade) => {
    setSelectedGrade(grade);
  };

  const handleContinueToRanking = async () => {
    if (!selectedGrade) return;
    
    // Fetch existing watched items with same grade, sorted by rank
    try {
      const response = await axios.get(`${API}/watched?media_type=movie&grade=${selectedGrade}`, { withCredentials: true });
      const existing = (response.data.series || []).sort((a, b) => a.rank_in_group - b.rank_in_group);
      
      if (existing.length === 0) {
        // No existing items, save directly at rank 1
        await saveWatched(selectedGrade, 1);
      } else {
        // Start binary search comparison
        setExistingItems(existing);
        setSearchLow(0);
        setSearchHigh(existing.length);
        setComparisonStep(true);
        
        // Start with middle item
        const midIndex = Math.floor(existing.length / 2);
        setComparisonItem(existing[midIndex]);
      }
    } catch (error) {
      toast.error('Failed to load rankings');
    }
  };

  const handleComparisonChoice = async (preferNew) => {
    // Binary search logic
    let low = searchLow;
    let high = searchHigh;
    
    if (preferNew) {
      // New item is better, search upper half (lower ranks = better)
      high = existingItems.findIndex(item => item.id === comparisonItem.id);
    } else {
      // Existing item is better, search lower half
      low = existingItems.findIndex(item => item.id === comparisonItem.id) + 1;
    }
    
    if (low >= high) {
      // Found the position
      const rank = low + 1; // Rank is 1-indexed
      setDeterminedRank(rank);
      await saveWatched(selectedGrade, rank);
    } else {
      // Continue binary search
      setSearchLow(low);
      setSearchHigh(high);
      const midIndex = Math.floor((low + high) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };

  const saveWatched = async (grade, rank) => {
    setSavingWatched(true);
    try {
      await axios.post(`${API}/watched/direct`, {
        tmdb_id: watchedItem.tmdb_id,
        media_type: 'movie',
        title: watchedItem.title,
        poster_path: watchedItem.poster_path,
        backdrop_path: watchedItem.backdrop_path,
        first_air_date: watchedItem.first_air_date,
        overview: watchedItem.overview,
        vote_average: watchedItem.vote_average,
        genres: watchedItem.genres || [],
        primary_genre: customGenre || primaryGenre,
        grade: grade,
        rank_in_group: rank,
        is_first_watch: isFirstWatch,
        is_big_screen: isBigScreen,
        watch_date: watchDate || null,
        comment: watchComment || null
      }, { withCredentials: true });
      
      toast.success(`"${watchedItem.title}" ranked #${rank} in your ${grade === 'liked' ? 'liked' : grade === 'alright' ? 'alright' : 'disliked'} films!`);
      resetWatchedDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingWatched(false);
    }
  };

  const resetWatchedDialog = () => {
    setWatchedItem(null);
    setSelectedGrade(null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setDeterminedRank(null);
    setSearchLow(0);
    setSearchHigh(0);
    setIsFirstWatch(true);
    setIsBigScreen(false);
    setWatchDate('');
    setWatchComment('');
    setPrimaryGenre(null);
    setCustomGenre('');
  };

  const grades = [
    { g: 'liked', icon: <ThumbsUp className="w-6 h-6" />, label: 'I liked it', cls: 'border-[#F87171] bg-[#F87171]/5 text-[#F87171] hover:bg-[#F87171]/10' },
    { g: 'alright', icon: <Meh className="w-6 h-6" />, label: 'It was alright', cls: 'border-amber-400 bg-amber-50 text-amber-600 hover:bg-amber-100' },
    { g: 'disliked', icon: <ThumbsDown className="w-6 h-6" />, label: "I didn't like it", cls: 'border-slate-400 bg-slate-50 text-slate-600 hover:bg-slate-100' }
  ];

  const getGradeLabel = (grade) => {
    if (grade === 'liked') return 'films you liked';
    if (grade === 'alright') return 'films that were alright';
    return "films you didn't like";
  };

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath="/films" />

      <main className="container mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
            <Sparkles className="w-6 h-6 text-[#F87171]" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">Discover Films</h1>
            <p className="text-white/80">Find your next favorite movie</p>
          </div>
        </div>

        {/* Search & Filters Card */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Search films..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full px-6">
                Search
              </Button>
              {mode === 'search' && (
                <Button type="button" variant="outline" onClick={clearSearch} className="rounded-full">
                  Clear
                </Button>
              )}
            </form>

            {mode === 'discover' && (
              <div className="space-y-4">
                {/* Curated Collections */}
                <div className="flex flex-wrap gap-2">
                  {COLLECTIONS.map((col) => {
                    const Icon = col.icon;
                    const isActive = activeCollection === col.value;
                    return (
                      <Button
                        key={col.value}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleCollectionClick(col.value)}
                        className={`rounded-full ${isActive ? 'bg-[#F87171] hover:bg-[#EF4444] text-white' : 'hover:border-[#F87171] hover:text-[#F87171]'}`}
                        data-testid={`collection-${col.value}`}
                      >
                        <Icon className="w-4 h-4 mr-1.5" />
                        {col.label}
                      </Button>
                    );
                  })}
                </div>
                
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Genre:</span>
                    <Select 
                      value={selectedGenre} 
                      onValueChange={(val) => { setSelectedGenre(val); setActiveCollection(null); setPage(1); }}
                      disabled={!!activeCollection}
                    >
                      <SelectTrigger className="w-[160px]" data-testid="genre-filter">
                        <SelectValue placeholder="All Genres" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Genres</SelectItem>
                        {genres.map((genre) => (
                          <SelectItem key={genre.id} value={genre.id.toString()}>
                            {genre.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Sort:</span>
                    <Select value={sortBy} onValueChange={(val) => { setSortBy(val); setPage(1); }} disabled={!!activeCollection}>
                      <SelectTrigger className="w-[160px]" data-testid="sort-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* English Only Toggle */}
                  <div className="flex items-center gap-2 ml-auto">
                    <Globe className="w-4 h-4 text-[#64748B]" />
                    <span className="text-sm text-[#64748B]">English only</span>
                    <Switch
                      checked={englishOnly}
                      onCheckedChange={(checked) => { setEnglishOnly(checked); setPage(1); }}
                      data-testid="english-only-toggle"
                    />
                  </div>
                </div>

                {/* Active Collection Info */}
                {activeCollection && (
                  <div className="flex items-center gap-2 p-3 bg-[#F87171]/10 rounded-lg border border-[#F87171]/20">
                    {(() => {
                      const col = COLLECTIONS.find(c => c.value === activeCollection);
                      const Icon = col?.icon || Award;
                      return (
                        <>
                          <Icon className="w-5 h-5 text-[#F87171]" />
                          <div>
                            <span className="font-medium text-[#3D4F6F]">{col?.label}</span>
                            <span className="text-sm text-[#64748B] ml-2">{col?.description}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setActiveCollection(null)} 
                            className="ml-auto text-[#64748B] hover:text-[#3D4F6F]"
                          >
                            Clear
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#F87171] animate-spin" />
              </div>
            ) : activeCollection === 'for-you' && personalRecommendations && !personalRecommendations.unlocked ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-[#F87171] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#3D4F6F] mb-2">Unlock AI Recommendations</h3>
                <p className="text-[#64748B] mb-4">
                  Rate {personalRecommendations.required_count - personalRecommendations.watched_count} more films to unlock personalized AI recommendations.
                </p>
                <p className="text-sm text-[#64748B]">
                  Progress: {personalRecommendations.watched_count} / {personalRecommendations.required_count} films rated
                </p>
                <Button 
                  onClick={() => { setActiveCollection(null); setMode('discover'); }} 
                  className="mt-4 bg-[#F87171] hover:bg-[#F87171]/90"
                >
                  Browse Films
                </Button>
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-12">
                <Film className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[#64748B]">No films found</p>
              </div>
            ) : (
              <>
                {/* AI Recommendations Header */}
                {activeCollection === 'for-you' && personalRecommendations?.profile && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      <h3 className="font-semibold text-[#3D4F6F]">Personalized For You</h3>
                    </div>
                    <div className="text-sm text-[#64748B] space-y-1">
                      <p><span className="font-medium">Top rated:</span> {personalRecommendations.profile.based_on?.slice(0, 3).join(', ')}</p>
                      <p><span className="font-medium">Favourite genres:</span> {personalRecommendations.profile.favorite_genres?.slice(0, 3).join(', ')}</p>
                      {personalRecommendations.profile.favorite_people?.length > 0 && (
                        <p><span className="font-medium">Favourite people:</span> {personalRecommendations.profile.favorite_people.slice(0, 3).map(p => p.name).join(', ')}</p>
                      )}
                    </div>
                    {personalRecommendations.recommendation_types && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {personalRecommendations.recommendation_types.map(rt => rt.count > 0 && (
                          <span key={rt.type} className="text-xs px-2 py-1 rounded-full bg-white border border-purple-200">
                            {rt.type === 'similar' && '🎬 Similar films'}
                            {rt.type === 'genre' && '🎭 By genre'}
                            {rt.type === 'person' && '⭐ By cast/crew'}
                            {' '}({rt.count})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {results.slice(0, 24).map((item) => (
                    <MediaCard 
                      key={item.tmdb_id} 
                      item={item}
                      mediaType="movie"
                      onClick={() => handleViewDetails(item)}
                      onAddToList={() => handleOpenAddToList(item)}
                      onMarkWatched={() => handleOpenWatched(item)}
                      testIdPrefix="film"
                    />
                  ))}
                </div>

                {mode === 'discover' && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                      className="rounded-full"
                    >
                      Previous
                    </Button>
                    <span className="text-[#64748B]">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                      className="rounded-full"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Series Details Dialog */}
      <SeriesDetailsDialog
        open={!!selectedSeries}
        onClose={() => { setSelectedSeries(null); setSeriesDetails(null); }}
        loading={loadingDetails}
        details={seriesDetails}
        onAddToList={(item) => {
          // Close details dialog and open add to list dialog
          setSelectedSeries(null);
          setSeriesDetails(null);
          setAddToListItem({
            tmdb_id: item.id || item.tmdb_id,
            title: item.title,
            poster_path: item.poster_path,
            genres: item.genres?.map(g => typeof g === 'string' ? { name: g } : g) || [],
            media_type: 'movie'
          });
        }}
      />

      {/* Add to List Dialog - Using reusable component */}
      <AddToListDialog
        open={!!addToListItem}
        onClose={() => { setAddToListItem(null); setSelectedListId(null); }}
        item={addToListItem}
        watchlists={watchlists}
        onAddToList={handleAddToListFromDialog}
        onAddAndRank={handleAddAndRankFromDialog}
        onCreateList={() => { setAddToListItem(null); navigate('/films'); }}
        loading={addingToList}
        mediaType="movie"
      />

      {/* Mark as Watched Dialog */}
      <Dialog open={!!watchedItem} onOpenChange={() => resetWatchedDialog()}>
        <DialogContent className="max-w-lg" data-testid="mark-watched-dialog">
          {!comparisonStep ? (
            // Step 1: Grade Selection
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-[#3D4F6F]">How was it?</DialogTitle>
                <DialogDescription className="text-[#64748B]">
                  Rate your experience watching &ldquo;{watchedItem?.title}&rdquo;
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Grade Selection */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#64748B] mb-2">How did you like it?</p>
                  {grades.map(({ g, icon, label, cls }) => (
                    <button 
                      key={g} 
                      data-testid={`grade-${g}-btn`} 
                      onClick={() => handleGradeSelect(g)} 
                      className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${
                        selectedGrade === g ? cls : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={selectedGrade === g ? '' : 'opacity-50'}>{icon}</div>
                      <span className={`font-medium ${selectedGrade === g ? '' : 'text-slate-500'}`}>{label}</span>
                      {selectedGrade === g && <Check className="w-5 h-5 ml-auto" />}
                    </button>
                  ))}
                </div>
                
                {/* First Time Watch & Big Screen Toggles - Horizontal */}
                <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setIsFirstWatch(!isFirstWatch)}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      isFirstWatch 
                        ? 'border-purple-400 bg-purple-50 text-purple-700' 
                        : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                    }`}
                    data-testid="first-watch-toggle"
                  >
                    <Eye className={`w-5 h-5 ${isFirstWatch ? 'text-purple-600' : 'text-slate-400'}`} />
                    <span className="font-medium text-sm">First watch</span>
                  </button>
                  <button
                    onClick={() => setIsBigScreen(!isBigScreen)}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      isBigScreen 
                        ? 'border-amber-400 bg-amber-50 text-amber-700' 
                        : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                    }`}
                    data-testid="big-screen-toggle"
                  >
                    <Clapperboard className={`w-5 h-5 ${isBigScreen ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className="font-medium text-sm">Big screen</span>
                  </button>
                </div>
                
                {/* Watch Date */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date watched
                  </label>
                  <Input
                    type="date"
                    value={watchDate}
                    onChange={(e) => setWatchDate(e.target.value)}
                    className="w-full"
                    data-testid="watch-date-input"
                  />
                </div>
                
                {/* Comments/Thoughts */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block">
                    Thoughts (optional)
                  </label>
                  <Textarea
                    placeholder="What did you think about it?"
                    value={watchComment}
                    onChange={(e) => setWatchComment(e.target.value)}
                    className="resize-none h-20"
                    data-testid="watch-comment-input"
                  />
                </div>
                
                {/* Primary Genre Selection */}
                <div className="pt-2">
                  <label className="text-sm font-medium text-[#64748B] mb-2 block">
                    Primary Genre (for stats)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(watchedItem?.genres || []).map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => { setPrimaryGenre(genre); setCustomGenre(''); }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          primaryGenre === genre && !customGenre
                            ? 'bg-[#F87171] text-white border-[#F87171]'
                            : 'bg-white text-[#64748B] border-slate-200 hover:border-[#F87171]'
                        }`}
                        data-testid={`genre-select-${genre}`}
                      >
                        {genre}
                      </button>
                    ))}
                    {/* Custom Genre Option */}
                    <button
                      type="button"
                      onClick={() => setPrimaryGenre('custom')}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        primaryGenre === 'custom' || customGenre
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-[#64748B] border-slate-200 hover:border-purple-400'
                      }`}
                      data-testid="genre-select-custom"
                    >
                      + Custom
                    </button>
                  </div>
                  {/* Custom Genre Input */}
                  {(primaryGenre === 'custom' || customGenre) && (
                    <div className="mt-2">
                      <Input
                        type="text"
                        placeholder="Enter your custom genre..."
                        value={customGenre}
                        onChange={(e) => setCustomGenre(e.target.value)}
                        className="text-sm"
                        data-testid="custom-genre-input"
                      />
                    </div>
                  )}
                </div>
                
                {/* Continue to Ranking Button */}
                <Button
                  onClick={handleContinueToRanking}
                  disabled={!selectedGrade || savingWatched}
                  className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full mt-2"
                  data-testid="continue-to-ranking-btn"
                >
                  {savingWatched ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  Continue to Ranking
                </Button>
              </div>
            </>
          ) : (
            // Step 2: Head-to-Head Comparison
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-[#3D4F6F] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Which do you prefer?
                </DialogTitle>
                <DialogDescription className="text-[#64748B]">
                  Comparing against your {getGradeLabel(selectedGrade)} ({existingItems.length} total)
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-6">
                <div className="grid grid-cols-2 gap-4">
                  {/* New Item */}
                  <button
                    onClick={() => handleComparisonChoice(true)}
                    disabled={savingWatched}
                    className="group flex flex-col items-center p-4 rounded-2xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all"
                    data-testid="prefer-new-btn"
                  >
                    <div className="relative aspect-[2/3] w-full max-w-[140px] rounded-lg overflow-hidden shadow-md mb-3">
                      {watchedItem?.poster_path ? (
                        <img src={watchedItem.poster_path} alt={watchedItem.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                          <Film className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 bg-[#F87171] text-white text-xs font-bold px-2 py-1 rounded-full">
                        NEW
                      </div>
                    </div>
                    <p className="font-medium text-[#3D4F6F] text-center text-sm line-clamp-2">{watchedItem?.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">Click if you prefer this</p>
                  </button>
                  
                  {/* Comparison Item */}
                  <button
                    onClick={() => handleComparisonChoice(false)}
                    disabled={savingWatched}
                    className="group flex flex-col items-center p-4 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                    data-testid="prefer-existing-btn"
                  >
                    <div className="relative aspect-[2/3] w-full max-w-[140px] rounded-lg overflow-hidden shadow-md mb-3">
                      {comparisonItem?.poster_path ? (
                        <img src={comparisonItem.poster_path} alt={comparisonItem.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                          <Film className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        #{comparisonItem?.rank_in_group || '?'}
                      </div>
                    </div>
                    <p className="font-medium text-[#3D4F6F] text-center text-sm line-clamp-2">{comparisonItem?.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">Click if you prefer this</p>
                  </button>
                </div>
                
                {savingWatched && (
                  <div className="flex items-center justify-center gap-2 mt-6 text-[#64748B]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving your ranking...</span>
                  </div>
                )}
                
                <div className="mt-6 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-[#64748B] text-center">
                    Narrowing down position... ({Math.ceil(Math.log2(existingItems.length + 1))} comparisons max)
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Rank New Series Dialog */}
      <RankNewSeriesDialog
        open={isRankNewOpen}
        onClose={() => {
          setIsRankNewOpen(false);
          setAddToListItem(null);
          setSelectedListId(null);
          setRankingListSeries([]);
          setAddingToList(false);
          toast.success(`Added "${addToListItem?.title}" to list!`);
        }}
        newSeries={addToListItem}
        allSeries={rankingListSeries}
        onSaveOrder={handleSaveRankOrder}
      />
    </div>
  );
}
